import { useEffect, useSyncExternalStore } from 'react'

/**
 * The service worker as the rest of the app sees it: whether a new version is waiting, and a way
 * to fetch and install one on request (the header's update chip). UpdatePrompt binds it.
 */
let registration: ServiceWorkerRegistration | undefined
let apply: ((reload: boolean) => Promise<void>) | undefined
let waiting = false
const listeners = new Set<() => void>()

function emit() {
  for (const l of listeners) l()
}

export function bindServiceWorker(
  reg: ServiceWorkerRegistration | undefined,
  applyUpdate: (reload: boolean) => Promise<void>,
): void {
  registration = reg
  apply = applyUpdate
}

export function setAppUpdateWaiting(value: boolean): void {
  if (waiting === value) return
  waiting = value
  emit()
}

/** True when a new app version is downloaded and waits for a reload. */
export function useAppUpdateWaiting(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => waiting,
    () => false,
  )
}

/** A service worker that is installing settles as installed (waiting) or redundant, or times out. */
function settle(worker: ServiceWorker, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer)
      worker.removeEventListener('statechange', onChange)
      resolve()
    }
    const onChange = () => {
      if (worker.state === 'installed' || worker.state === 'redundant') done()
    }
    const timer = setTimeout(done, timeoutMs)
    worker.addEventListener('statechange', onChange)
    onChange()
  })
}

export type AppUpdateResult = 'reloading' | 'current' | 'unavailable'

/**
 * Ask the server for a newer app and, when there is one, install it and reload. Only call this
 * where a reload loses nothing (never during a delivery).
 */
export async function installAppUpdate(timeoutMs = 30_000): Promise<AppUpdateResult> {
  if (!registration || !apply) return 'unavailable'
  await registration.update()
  if (registration.installing) await settle(registration.installing, timeoutMs)
  if (!registration.waiting && !waiting) return 'current'
  await apply(true)
  return 'reloading'
}

// ---------- silent updates ----------

/** Screens where a reload loses nothing (Home, Sync, More) say so while they are shown. */
let safeScreens = 0
const safeListeners = new Set<() => void>()

/** Call on a screen where reloading loses nothing. Never on a delivery or task screen. */
export function useSafeToUpdate(active: boolean): void {
  useEffect(() => {
    if (!active) return
    safeScreens += 1
    safeListeners.forEach((l) => l())
    return () => {
      safeScreens -= 1
      safeListeners.forEach((l) => l())
    }
  }, [active])
}

export function onSafeToUpdateChange(l: () => void): () => void {
  safeListeners.add(l)
  return () => safeListeners.delete(l)
}

/** A safe screen is shown and nobody is typing on it. */
export function isSafeToUpdate(doc: Document = document): boolean {
  if (safeScreens <= 0) return false
  const el = doc.activeElement as HTMLElement | null
  const typing =
    !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
  return !typing
}

/**
 * When a waiting update may be put in without anyone noticing: on a safe screen, or while the app
 * is in the background (the person sees the new version when they come back to it).
 */
export function mayApplySilently(doc: Document = document): boolean {
  return doc.visibilityState === 'hidden' || isSafeToUpdate(doc)
}

/** Tests only. */
export function resetAppUpdate(): void {
  registration = undefined
  apply = undefined
  waiting = false
  safeScreens = 0
  emit()
}
